import styled, { css } from 'styled-components';

export const gradientText = css`
  background: linear-gradient(90deg,#00f7d2,#5ce9ff,#fff);
  -webkit-background-clip: text;
  color: transparent;
`;

export const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

export const MainContent = styled.main`
  flex: 1;
  display: flex;
  justify-content: center;
  padding: 5rem 1.5rem 3rem;
`;

export const ContentInner = styled.div`
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.75rem;
  text-align: center;
`;

export const Headline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

export const Title = styled.h1`
  margin: 0;
  font-size: 3rem;
  line-height: 1.3;
  font-weight: 900;
  ${gradientText};
`;

export const Subtitle = styled.p`
  margin: 0;
  color: #9aa5be;
  font-size: 1rem;
`;

export const Tabs = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
`;

export const TabButton = styled.button`
  min-width: 140px;
  padding: 12px 26px;
  border-radius: 18px;
  border: ${({ active }) => (active ? '2px solid rgba(92,233,255,0.75)' : '1px solid rgba(148,163,184,0.25)')};
  background: ${({ active }) => (active
    ? 'linear-gradient(135deg,#00f7d2,#5ce9ff)'
    : 'rgba(17,21,34,0.88)')};
  color: ${({ active }) => (active ? '#001018' : '#e5e7eb')};
  font-weight: 700;
  font-size: 1rem;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover{
    transform: translateY(-2px);
  }
`;

export const Card = styled.section`
  width: 100%;
  max-width: 360px;
  background: linear-gradient(145deg,#121829 0%,#0b101d 100%);
  border-radius: 28px;
  padding: 28px 26px;
  border: 1px solid rgba(92,233,255,0.1);
  box-shadow: 0 28px 60px rgba(3,7,20,0.55);
  text-align: left;
  position: relative;
`;

export const LoaderOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: ${({ visible }) => (visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  background: rgba(10, 14, 24, 0.68);
  border-radius: inherit;
  z-index: 5;
`;

export const SectionSpacing = styled.div`
  margin-top: 1.75rem;
`;

export const StepIndicator = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #7b8aa6;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 1rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  margin-top: 1rem;
`;

export const Label = styled.label`
  font-size: 15px;
  font-weight: 700;
  color: #edf2ff;
`;

const fieldBase = css`
  width: 100%;
  padding: 12px 16px;
  border-radius: 14px;
  border: 1px solid rgba(92,233,255,0.12);
  background: rgba(9,12,22,0.85);
  color: #d6def5;
  font-size: 15px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &::placeholder{
    color: rgba(148,163,184,0.55);
  }

  &:focus{
    outline: none;
    border-color: rgba(92,233,255,0.5);
    box-shadow: 0 0 0 3px rgba(92,233,255,0.15);
  }
`;

export const Input = styled.input`
  ${fieldBase};
`;

export const Select = styled.select`
  ${fieldBase};
`;

export const Hint = styled.div`
  font-size: 12px;
  color: rgba(148,163,184,0.8);
  margin-top: 0.4rem;
`;

export const Message = styled.div`
  margin-top: 1rem;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${({ positive }) => (positive ? '#34d399' : '#fb7185')};
`;

export const PasswordTrack = styled.div`
  height: 6px;
  border-radius: 9999px;
  background: rgba(148,163,184,0.18);
  overflow: hidden;
  margin-top: 0.65rem;
`;

export const PasswordFill = styled.div`
  height: 6px;
  border-radius: 9999px;
  transition: width 0.2s ease;
`;

export const Guidelines = styled.ul`
  margin: 0.6rem 0 0;
  padding-left: 20px;
  color: rgba(148,163,184,0.8);
  font-size: 12px;
  line-height: 1.6;
  text-align: left;
`;

export const Actions = styled.div`
  margin-top: 1.5rem;
  display: flex;
  gap: 0.75rem;
  justify-content: space-between;
`;

export const ActionButton = styled.button`
  flex: 1;
  padding: 14px 18px;
  border-radius: 18px;
  border: ${({ primary }) => (primary ? '1px solid rgba(92,233,255,0.45)' : '1px solid rgba(92,233,255,0.2)')};
  background: ${({ primary }) => (primary ? 'linear-gradient(135deg,#00f7d2,#5ce9ff)' : 'rgba(13,18,32,0.88)')};
  color: ${({ primary }) => (primary ? '#001018' : '#5ce9ff')};
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover{
    transform: translateY(-2px);
  }
`;

export const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

export const ToggleButton = styled.button`
  position: absolute;
  right: 14px;
  background: transparent;
  border: none;
  color: rgba(148,163,184,0.75);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover{
    color: #5ce9ff;
  }
`;

